import Uri from 'jsuri';
import { IConfig } from '@rhc-shared-components/control-wrapper';
import { IPageState } from '@rhc-shared-components/table-toolbar/dist/models/GeneralModels';
import Cookies from 'js-cookie';
import {
  IAgreement,
  ICompanyDetails,
  Iindustries,
  IOnBoardingStatusResponse,
  IPartnerSupportAccount,
  IPartnerSupportCases,
  IProfileContactRole,
  IProfileDetails,
  ITeamMembersResponse,
} from '../constants/apiconstants-hydra';
import Env from '../utils/env';
import {
  deleteUriWithoutAcceptType,
  getUri,
  getUriWithoutAcceptType,
  patchUri,
  postUri,
  postUriWithoutAcceptType,
} from '../utils/fetch';

const getTaticIdObj = () => {
  const internalTacticId = Cookies.get('rh_omni_itc');
  const externalTacticId = Cookies.get('rh_omni_tc');
  const personalizationTacticId = Cookies.get('rh_omni_pc');
  return {
    ...(internalTacticId && { internalTacticId }),
    ...(externalTacticId && { externalTacticId }),
    ...(personalizationTacticId && { personalizationTacticId }),
  };
};

// eslint-disable-next-line import/prefer-default-export
export const getAgreements = (): Promise<IAgreement[]> => {
  const uri = `${Env.hydraURL}${Env.hydraAPIPrefix}/companies/current/terms/status`;
  return getUri<IAgreement[]>(uri);
};
export const fetchProfileDetails = (): Promise<IProfileDetails> => {
  const uri = `${Env.hydraURL}${Env.hydraAPIPrefix}/contacts/current`;
  return getUri<IProfileDetails>(uri);
};
export const fetchContactRoles = (): Promise<IProfileContactRole> => {
  const uri = `${Env.hydraURL}${Env.hydraAPIPrefix}/metadata/contact_role`;
  return getUri<IProfileContactRole>(uri);
};
export const fileUploadURL = () => {
  return `${Env.hydraURL}${Env.hydraAPIPrefix}/contacts/current/picture`;
};
export const uploadProfilePicture = (fileData: Blob) => {
  const formData = new FormData();
  formData.append('file', fileData);
  const url = fileUploadURL();
  return postUriWithoutAcceptType(url, formData);
};
export const getProfilePictureRequest: () => Promise<Blob> = () => {
  const url = fileUploadURL();
  return getUriWithoutAcceptType(
    url,
    {},
    {
      dataType: 'image',
    }
  );
};

export const deleteProfilePicture = () => {
  const url = fileUploadURL();
  return deleteUriWithoutAcceptType(url);
};
export const patchCurrentContact = (details): Promise<IProfileContactRole> => {
  const uri = `${Env.hydraURL}${Env.hydraAPIPrefix}/contacts/current`;

  return patchUri(uri, {
    ...details,
    ...getTaticIdObj(),
  });
};
export const getOnboardingStatus = (
  abortSignal: AbortSignal
): Promise<IOnBoardingStatusResponse> => {
  const uri = new Uri(
    `${Env.hydraURL}${Env.hydraAPIPrefix}/companies/current/onboarding/status`
  );
  const additionalParams: RequestInit = {};
  if (abortSignal) {
    Object.assign(additionalParams, { signal: abortSignal });
  }
  const taticData = getTaticIdObj();
  if (Object.keys(taticData).length > 0) {
    const taticHeaders: HeadersInit = {
      ...(taticData?.internalTacticId && {
        'x-rh-omni-itc': taticData.internalTacticId,
      }),
      ...(taticData?.externalTacticId && {
        'x-rh-omni-tc': taticData.externalTacticId,
      }),
      ...(taticData?.personalizationTacticId && {
        'x-rh-omni-pc': taticData.personalizationTacticId,
      }),
    };
    Object.assign(additionalParams, { headers: taticHeaders });
  }
  return getUri<IOnBoardingStatusResponse>(uri, additionalParams);
};

export const getTeamMembers = (
  pagination?: IPageState,
  extraParams?: {
    includeDeactivatedMembers?: boolean;
    sortParams?: { sortBy: string; direction: string };
  },
  searchKey?: string,
  abortSignal?: AbortSignal
): Promise<ITeamMembersResponse> => {
  const additionalParams: RequestInit = {};
  const uri = new Uri(
    `${Env.hydraURL}${Env.hydraAPIPrefix}/companies/current/contacts`
  );
  uri.addQueryParam('limit', pagination.perPage);
  uri.addQueryParam('offset', (pagination.page - 1) * pagination.perPage);
  uri.addQueryParam('match', 'all');
  if (abortSignal) {
    Object.assign(additionalParams, { signal: abortSignal });
  }
  if (extraParams?.sortParams) {
    uri.addQueryParam('sortby', extraParams?.sortParams?.sortBy);
    uri.addQueryParam(
      'sortdirection',
      extraParams?.sortParams?.direction.toUpperCase()
    );
  }
  if (extraParams?.includeDeactivatedMembers !== undefined) {
    uri.addQueryParam('isDeactivated', extraParams?.includeDeactivatedMembers);
  }
  if (searchKey) {
    uri.addQueryParam('searchKey', searchKey);
  }
  return getUri(uri, additionalParams);
};

export const getMetaDataIndustries = (): Promise<Iindustries> => {
  const uri = `${Env.hydraURL}${Env.hydraAPIPrefix}/metadata/industry`;
  return getUri<Iindustries>(uri);
};

export const fetchCompanyProfileDetails = (): Promise<ICompanyDetails> => {
  const uri = `${Env.hydraURL}${Env.hydraAPIPrefix}/companies/current`;
  return getUri<ICompanyDetails>(uri);
};

export const postCompanyLogoApiUrl = () => {
  return `${Env.hydraURL}${Env.hydraAPIPrefix}/companies/current/logo`;
};

export const uploadCompanyLogo = (fileData: Blob) => {
  const formData = new FormData();
  formData.append('file', fileData);
  const url = postCompanyLogoApiUrl();
  return postUriWithoutAcceptType(url, formData);
};

export const patchCompanyProfileDetails = (
  companyDetails
): Promise<ICompanyDetails> => {
  const uri = `${Env.hydraURL}${Env.hydraAPIPrefix}/companies/current`;
  return patchUri(uri, {
    ...companyDetails,
    ...getTaticIdObj(),
  });
};

export const patchProfileOnboardingDetails = (
  companyDetails
): Promise<ICompanyDetails> => {
  const uri = `${Env.hydraURL}${Env.hydraAPIPrefix}/companies/current/onboarding`;
  return postUri(uri, {
    ...companyDetails,
    ...getTaticIdObj(),
  });
};

// GETs the config values
export const getRhpaConfig = (): Promise<IConfig[]> => {
  const uri = Env.hydraProxyURLPreprodStage
    .clone()
    .setPath(`${Env.hydraRestPrefix}/rhpa_config`);
  return getUri(uri);
};

// Checks if the partner has an account with SFDC

export const fetchPartnerSFDCAccount = (): Promise<IPartnerSupportAccount> => {
  const uri = Env.hydraProxyURL
    .clone()
    .setPath(`${Env.hydraProxyRestPrefix}/accounts/current`);
  return getUri<IPartnerSupportAccount>(uri);
};

// fetches partners support cases
export const fetchPartnerSupportCases = (
  partnerSearch
): Promise<IPartnerSupportCases> => {
  const uri = Env.hydraProxyURL
    .clone()
    .setPath(`${Env.hydraProxyRestPrefix}/cases/filter?partnerSearch=true`);
  return postUri<IPartnerSupportCases>(uri, partnerSearch);
};
